import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ActivatedRoute } from '@freelancer/activated-route';
import { Location } from '@freelancer/location';
import { isDefined } from '@freelancer/utils';
import type { Observable } from 'rxjs';
import { BehaviorSubject } from 'rxjs';
import { filter, map, startWith } from 'rxjs/operators';
import { FakeLocalizationLanguage } from './fake-localization.model';

//  This service was built to support switching between two different language query params,
//  intended to be English - EN and Arabic - AR for the Saudi Arabia BAHR Inititative landing page.
//  Components that use this service should provide the translations themselves, as this
//  service indicates that a translation should be used, but not what that translation will be.
@Injectable({ providedIn: 'root' })
export class FakeLocalizationService {
  private fakeLocaleSubject$: BehaviorSubject<FakeLocalizationLanguage>;

  constructor(
    private location: Location,
    private router: Router,
    private route: ActivatedRoute,
  ) {}

  private initiate(): void {
    const langParam = new URL(this.location.href).searchParams.get('lang');

    const langCode =
      langParam === 'ar'
        ? FakeLocalizationLanguage.AR
        : FakeLocalizationLanguage.EN;
    this.fakeLocaleSubject$ = new BehaviorSubject(langCode);
  }

  // Get an observable of the current FakeLocalizationLanguage code, which is the selected language
  fakeLocale(): Observable<FakeLocalizationLanguage> {
    if (!isDefined(this.fakeLocaleSubject$)) {
      this.initiate();
    }
    return this.fakeLocaleSubject$.asObservable();
  }

  // Switch between the EN and AR language codes, and navigate to the current url
  updateFakeLocale(): void {
    if (!isDefined(this.fakeLocaleSubject$)) {
      this.initiate();
    }

    // Toggle to other language option
    const langParam = new URL(this.location.href).searchParams.get('lang');
    const queryParams = langParam ? { lang: undefined } : { lang: 'ar' };

    this.router.navigate([], {
      relativeTo: this.route.activatedRoute,
      queryParams,
      queryParamsHandling: 'merge',
    });

    // Update subject to have the switched language code
    const langCode =
      langParam === 'ar'
        ? FakeLocalizationLanguage.EN
        : FakeLocalizationLanguage.AR;
    this.fakeLocaleSubject$.next(langCode);
  }

  // Get an observable of the reading direction.
  readingDirection(): Observable<string> {
    return this.fakeLocale().pipe(
      filter(isDefined),
      map(activeLanguage =>
        activeLanguage === FakeLocalizationLanguage.AR ? 'rtl' : 'ltr',
      ),
      startWith('ltr'),
    );
  }
}
